/**
 * CandidateTPPage -- Time Priority Page for Candidates
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 * Route: /candidate/tp
 *
 * Reuses the Step 6F CandidateTP component (ActivitySlider, BucketAccordion,
 * TPProgressBar). This page handles data fetching and submission while
 * delegating rendering to the 6F component.
 */

import React, { useEffect, useState, useCallback } from "react";
import { CandidateTP } from "../../components/CandidateTP";
import { showToast } from "../../lib/toast";
import { describeError } from "../../lib/errorMessages";
import { apiClient } from "../../services/apiClient";
import { withActiveTestId } from "../../stores/activeTestStore";
import type { Activity, Bucket } from "../../types";

interface AssessmentInfo {
  test?: { id: string; name?: string; content_version_id?: string };
  status?: string;
}

export interface CandidateTPPageProps {
  locked?: boolean;
  onSubmit?: () => void;
}

export function CandidateTPPage({ locked: lockedProp, onSubmit }: CandidateTPPageProps = {}) {
  const [activitiesByBucket, setActivitiesByBucket] = useState<Array<{ bucket: Bucket; activities: Activity[] }>>([]);
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testId, setTestId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    apiClient
      .get<AssessmentInfo>(withActiveTestId("/api/candidate/assessment"))
      .then(async (assessmentData) => {
        if (cancelled) return;
        const tid = assessmentData?.test?.id;
        if (tid) setTestId(tid);

        // Single candidate-scoped endpoint returns activities + buckets for
        // the candidate's actionable assessment. /api/activities and
        // /api/buckets are content-role-gated (403 for Candidate), so we
        // do not call them directly.
        // In parallel, fetch any previously submitted TP allocations so the
        // candidate sees their saved values pre-filled. Without this the
        // page initialised every slider to zero, so any return visit (e.g.
        // after a session interruption that bounced them back to /tp) made
        // the candidate look unsaved and forced them to redo the allocation
        // from scratch.
        const [tpRes, stateRes] = await Promise.all([
          apiClient
            .get<{ activities: Activity[]; buckets: Bucket[] }>(
              tid ? `/api/candidate/tp/activities?test_id=${tid}` : "/api/candidate/tp/activities"
            )
            .catch(() => ({ activities: [] as Activity[], buckets: [] as Bucket[] })),
          tid
            ? apiClient
                .get<{ allocations: Array<{ activity_id: string; points: number }> }>(`/api/candidate/tp/${tid}`)
                .catch(() => ({ allocations: [] as Array<{ activity_id: string; points: number }> }))
            : Promise.resolve({ allocations: [] as Array<{ activity_id: string; points: number }> }),
        ]);
        if (cancelled) return;

        const acts = tpRes.activities ?? [];
        const bucketList = tpRes.buckets ?? [];

        if (acts && acts.length > 0) {
          const bucketLookup = new Map(bucketList.map((b) => [b.id, b]));

          // Group activities by bucket
          const bucketMap = new Map<string, { bucket: Bucket; activities: Activity[] }>();
          for (const act of acts) {
            const bid = act.bucket_id ?? "default";
            if (!bucketMap.has(bid)) {
              const existing = bucketLookup.get(bid);
              bucketMap.set(bid, {
                bucket: existing ?? { id: bid, name: "Activities", version_id: "" },
                activities: [],
              });
            }
            bucketMap.get(bid)!.activities.push(act);
          }
          setActivitiesByBucket(Array.from(bucketMap.values()));

          const savedByActivity = new Map(
            (stateRes.allocations ?? []).map((a) => [a.activity_id, Number(a.points) || 0])
          );
          const initial: Record<string, number> = {};
          acts.forEach((a: Activity) => {
            initial[a.id] = savedByActivity.get(a.id) ?? 0;
          });
          setAllocations(initial);
        }
        return undefined;
      })
      .catch((err) => {
        if (!cancelled) setError(describeError(err, "Failed to load"));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const total = Object.values(allocations).reduce((sum, v) => sum + v, 0);

  const handleAllocate = useCallback((actId: string, value: number) => {
    setAllocations((prev) => {
      const otherTotal = Object.entries(prev)
        .filter(([id]) => id !== actId)
        .reduce((sum, [, v]) => sum + v, 0);
      const clamped = Math.max(0, Math.min(value, 100 - otherTotal));
      return { ...prev, [actId]: clamped };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!testId) return;
    setIsSubmitting(true);
    try {
      const allocationArray = Object.entries(allocations).map(([activity_id, points]) => ({
        activity_id,
        points,
      }));
      // Candidate-scoped TP submit (PUT). The /api/tests/{id}/tp route is
      // gated to Test Administrator and 403s for candidates.
      await apiClient.put(`/api/candidate/tp/${testId}/submit`, { allocations: allocationArray });
      onSubmit?.();
      showToast({
        variant: "success",
        title: "Time priority submitted",
        description: "Your allocations have been saved.",
      });
    } catch (err) {
      // Previously a bare `catch {}` discarded the backend code entirely, so a
      // candidate hitting TP_IMMUTABLE_AFTER_AQ / TP_ALREADY_LOCKED /
      // TEST_NOT_ACCESSIBLE / a sum!=100 VALIDATION_ERROR just saw a generic
      // line. describeError surfaces the right explanation.
      showToast({
        variant: "error",
        title: "Submission failed",
        description: describeError(err, "Could not submit your allocations. Please try again."),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [testId, allocations, onSubmit]);

  return (
    <main className="max-w-5xl mx-auto py-8 px-4" style={{ minHeight: "600px" }}>
      <CandidateTP
        activitiesByBucket={activitiesByBucket}
        allocations={allocations}
        total={total}
        onAllocate={handleAllocate}
        onSubmit={handleSubmit}
        isLocked={lockedProp}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        error={error}
      />
    </main>
  );
}

export default CandidateTPPage;
